<script lang="ts" setup>
import { useRoute } from 'vue-router';
import NoPermissions from '@/modules/errors/NoPermissions.vue';
import useHasPermissions from '@/modules/current-user/permissions';
import type Permission from '@/modules/current-user/permissions/permissions';

// Lives in its own component (not App.vue) so the org soft reload's isSwitchingOrg
// toggle remounts it, giving useHasPermissions a fresh observer for the new org.
// In App.vue (the root, never remounted) the observer keeps the previous org's
// permissions after the cache is cleared, wrongly gating the user out.
const {
  isReady,
  hasAnyOfPermissions,
  hasClientPermissions,
  hasSupplierPermissions,
  hasFederatedPermissions,
} = useHasPermissions();

const route = useRoute();
const canAccess = computed(() => {
  if (!isReady.value) return true;

  // We do it like this so that parent routes get merged with children
  const permissions: Permission[] = [];
  let mode = '';
  for (const match of route.matched) {
    if (Array.isArray(match.meta.permissions)) {
      permissions.push(...match.meta.permissions);
    }
    if (typeof match.meta.mode === 'string') mode = match.meta.mode;
  }

  if (
    (mode === 'client' && !hasClientPermissions.value) ||
    (mode === 'supplier' && !hasSupplierPermissions.value) ||
    (mode === 'federated' && !hasFederatedPermissions.value)
  ) {
    return false;
  }

  if (!permissions.length) return true;

  return hasAnyOfPermissions(...(permissions as Permission[]));
});
</script>

<template>
  <RouterView v-if="canAccess" class="grow" />
  <NoPermissions v-else />
</template>
